import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Link } from 'react-router-dom';

import { connect } from 'react-redux';

import find from 'lodash/find';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';
import { useGetProductIngredientsListModal } from 'hooks/useGetProductIngredients';
import { useGoToCheckout } from 'hooks/useGoToCheckout';
import { makeStyles } from 'legacyStyles';

import CheckoutTicketV2 from 'Apps/Checkout/Blocks/CheckoutTicketV2';

import ClinicalClaimsModal from 'Components/ClinicalClaimsModal';
import IngredientsListModal from 'Components/IngredientsListModal';
import MembershipModal from 'Components/MembershipModal';
import ReviewRefineTag from 'Components/ReviewRefineTag';
import { RoutineSetModal } from 'Components/RoutineSetModal';
import Spacer from 'Components/Spacer';
import { SupplementsFactsModal } from 'Components/SupplementsFactsModal';
import { Typography } from 'Components/Typography';

import { useSkincareMinisFrequencyAbTest } from 'abTesting/skincareMinisFrequency/hook';
import { useSssShippingFeeAbTest } from 'abTesting/sssShippingFee/hook';

import { cartSectionName } from 'constants/cart';
import { productsSlugs } from 'constants/products';
import { FREE } from 'constants/skincareFragrances';

import { GWPBlock } from 'Blocks/GWPBlock';
import ProductCardCheckout from '../Blocks/ProductCardCheckout';

import ScrollTracker from 'Containers/ScrollTracker';

import checkoutCategoryProp from 'PropTypes/checkoutProps';
import consultationCountriesProps from 'PropTypes/countries';

import skincareMinisContent from 'assets/content/cart/skincareMinis';

import { getFragranceName } from 'utils/fragrances';
import { genitive } from 'utils/textUtils';
import useResponsiveVariant from 'utils/useResponsiveVariant';

import * as trackingActions from 'dux/tracking/actions';
import * as checkoutCartSelectors from 'dux/checkoutCart/selectors';
import * as userSelectors from 'dux/user/selectors';
import { CONTROL, VARIANT1 } from 'dux/featureFlags/constants/flagVariants';

import { useSkincareMinisAtCart } from '../hooks/useSkincareMinisAtCart';
import BaseScene from './BaseScene';
import { FeaturedSection, OptionalSection, UpsaleSection } from './CartSection';
import PreviewSection from './PreviewSection';
import { ProductsSetCardCheckout } from './ProductsSetCardCheckout';
import StyledCartSection from './StyledCartSection';

const Breadcrumbs = styled.div`
  display: flex;

  padding: ${legacyTheme.spacing.s24};
  padding-bottom: 0;

  ${legacyTheme.breakpoints.up('md')} {
    padding: ${legacyTheme.spacing.s32};
    padding-bottom: 0;
  }
`;

const BreadcrumbLink = styled(Link)`
  position: relative;

  color: ${theme.colors.primary[400]};

  :hover::after {
    position: absolute;
    bottom: 3px;
    left: 0;

    height: 1px;
    width: 100%;
    background: ${theme.colors.primary[400]};

    content: '';

    transition: all 0.3s ease-out;
  }

  span {
    text-transform: uppercase;
    font-size: ${legacyTheme.spacing.s12};
  }
`;

const GWPSection = styled(StyledCartSection)`
  padding-top: ${legacyTheme.props.minimumBlockPadding};
`;

const DarkBackground = styled.span`
  grid-column-start: 2;
  background: ${theme.colors.neutral[300]};
`;

const Desc = styled(Typography)`
  display: block;
  width: calc(140px + 30vw);
  ${legacyTheme.breakpoints.match('md')} {
    width: inherit;
  }
`;

const ProductsSetCardContainer = styled.div`
  margin-top: 40px;
`;

const useStyles = makeStyles({
  root: {
    display: 'grid',
    gridTemplateColumns: '60% 40%',
    alignItems: 'stretch',
    [legacyTheme.breakpoints.match('sm')]: {
      display: 'block',
    },
  },
});

const Cart = ({
  cart,
  checkoutCategory,
  content,
  disabled,
  featuredProducts,
  hasAnsweredStylingGelQuestions,
  hasUnansweredQuestions,
  arePreviewProductsInOptional,
  productsWithUnansweredQuestionsList,
  isCartSubscribeable,
  isReceivingGift,
  isSaving,
  optionalProducts,
  preselectedProducts,
  recommendedProducts,
  showPreviewSection,
  trackMembershipModalViewed,
  upsaleProducts,
  userFirstname,
  giftWithPurchaseListInCatalog,
  consultationProfileCountry,
  trackCartViewed,
  hasCompletedAllConsultations,
  hasFeedbackFromCheckoutCategory,
  currency,
  updateStatusIsLoading,
}) => {
  const {
    hasSkincareMinisInCatalog,
    skincareMinisProductsFromCatalog,
    hasSkincareMinisInItems,
    onAddSkincareMinis,
    onDeleteSkincareMinis,
    skincareMinisRecommendedSectionFromCatalog,
  } = useSkincareMinisAtCart();
  const { classes } = useStyles();
  const [productModalOpen, setProductModalOpen] = useState(null);
  const [clickedProducts, setClickedProducts] = useState(null);
  const { goToNextCheckoutStep } = useGoToCheckout();

  const shouldAnswerStylingGelQuestions = !hasAnsweredStylingGelQuestions;
  const { variantName: skincareMinisFrequencyAbTestVariant } = useSkincareMinisFrequencyAbTest({
    isTrackingOnMount: true,
  });

  const { variantName: sssShippingFeeAbTestVariantName } = useSssShippingFeeAbTest({
    isTrackingOnMount: hasSkincareMinisInCatalog,
  });

  const { isMobile } = useResponsiveVariant();

  const supplementsFactsVariant = cart?.catalog.find(
    el => el.type === productsSlugs.SUPPLEMENT_CORE
  )?.slug;

  useEffect(() => {
    trackCartViewed(checkoutCategory);
  }, []);

  useEffect(() => {
    if (productModalOpen === 'subscription') {
      trackMembershipModalViewed();
    }
  }, [productModalOpen]);

  const closeModal = () => setProductModalOpen(null);

  const openModal = (context, products = []) => {
    setProductModalOpen(context);
    setClickedProducts(products);
  };

  const onClickSkincareMinisModal = (context, products) => {
    openModal(context, products);
  };
  const renderProductsWithSubscription = (products, sectionName) => {
    if (hasSkincareMinisInCatalog && skincareMinisRecommendedSectionFromCatalog === sectionName) {
      return (
        <ProductsSetCardContainer>
          <ProductsSetCardCheckout
            backgroundImage={skincareMinisContent.backgroundImage}
            dataFrom="skincare-starter-set-card"
            description={skincareMinisContent.description}
            fragranceLabel={
              products?.[0].fragrance === FREE
                ? getFragranceName(products?.[0].fragrance)
                : `Fragrance: ${getFragranceName(products?.[0].fragrance)}`
            }
            isLoading={updateStatusIsLoading}
            modalButtonList={skincareMinisContent.modalButtonList.map(({ label, context }) => ({
              label,
              onClick: () =>
                onClickSkincareMinisModal(
                  context,
                  skincareMinisProductsFromCatalog.map(({ type }) => type)
                ),
            }))}
            onClickAdd={onAddSkincareMinis}
            onDelete={onDeleteSkincareMinis}
            productsList={skincareMinisProductsFromCatalog}
            selected={hasSkincareMinisInItems}
            selectedContent={{
              ...skincareMinisContent.selectedContent[
                skincareMinisFrequencyAbTestVariant === VARIANT1 ? VARIANT1 : CONTROL
              ],
              //  TODO: when both skincare-minis-frequency-ab-test and sss-shipping-fee-ab-test  ab tests are done this logic should be removed and the strings added to the content file
              description: (
                <>
                  {
                    skincareMinisContent.selectedContent[
                      skincareMinisFrequencyAbTestVariant === VARIANT1 ? VARIANT1 : CONTROL
                    ].description
                  }
                  {sssShippingFeeAbTestVariantName === VARIANT1 && (
                    <>{!isMobile ? <br /> : ' '}Just pay shipping; future orders ship free.</>
                  )}
                </>
              ),
              onClickMoreInfoModal: () => openModal('subscription'),
            }}
            stickerLabel={skincareMinisContent.stickerLabel[currency]}
            title={`${genitive(userFirstname)} ${skincareMinisContent.title}`}
          />
        </ProductsSetCardContainer>
      );
    }
    return products.map(product => {
      const disabledForStylingGel =
        shouldAnswerStylingGelQuestions && product.type === productsSlugs.STYLING_GEL;

      return (
        <ProductCardCheckout
          key={`${product.type}-with-subscription`}
          checkoutCategory={checkoutCategory}
          consultationProfileCountry={consultationProfileCountry}
          defaultIsOpenedExtendableSection={sectionName === cartSectionName.RECOMMENDED}
          disabled={disabledForStylingGel}
          fromGiftSet={isReceivingGift}
          hasFirstNameInLabel
          onModalOpen={context => openModal(context, [product.type])}
          product={product}
        />
      );
    });
  };

  const { incisList, incisError, incisStatus } = useGetProductIngredientsListModal(
    productModalOpen === 'ingredients',
    checkoutCategory
  );

  return (
    <BaseScene
      classes={{ content: classes.root }}
      footer
      FooterProps={{
        hideCTA: true,
        paddedOnRightOnDesktop: true,
      }}
    >
      <div>
        <ScrollTracker>
          {isCartSubscribeable && (
            <MembershipModal
              category={checkoutCategory}
              closeModal={() => closeModal()}
              isModalOpen={productModalOpen === 'subscription'}
            />
          )}
          {incisList && (
            <IngredientsListModal
              hasError={Boolean(incisError)}
              isLoading={['loading', 'idle'].includes(incisStatus)}
              isOpen={productModalOpen === 'ingredients'}
              name={userFirstname}
              onClose={() => closeModal({ context: 'ingredients' })}
              products={incisList.filter(product =>
                find(clickedProducts, type => type === product.type)
              )}
            />
          )}

          <ClinicalClaimsModal
            isOpen={productModalOpen === 'clinical'}
            onClose={() => closeModal({ context: 'clinical' })}
            product={clickedProducts?.[0]}
          />

          {skincareMinisProductsFromCatalog && (
            <RoutineSetModal
              isOpen={productModalOpen === 'routine'}
              onClose={() => closeModal({ context: 'routine' })}
              products={skincareMinisProductsFromCatalog}
              title="skincare starter set"
            />
          )}

          {supplementsFactsVariant && (
            <SupplementsFactsModal
              closeModal={closeModal}
              firstname={userFirstname}
              isModalOpen={productModalOpen === 'supplements'}
              variantSlug={supplementsFactsVariant}
            />
          )}

          {hasCompletedAllConsultations && (
            <Breadcrumbs>
              <BreadcrumbLink data-testid="breadcrumb-cart" to="/account/reorder">
                <Typography variant="mono3">All Carts</Typography>
              </BreadcrumbLink>
              <Spacer size={10} />
              <Typography
                color="grey"
                data-testid={`breadcrumb-cart-${checkoutCategory}`}
                variant="mono3"
              >
                / {checkoutCategory} Cart
              </Typography>
            </Breadcrumbs>
          )}
          {giftWithPurchaseListInCatalog?.length > 0 && (
            <GWPSection data-testid="catalog-gwp-section">
              {giftWithPurchaseListInCatalog.map(gwp => (
                <GWPBlock
                  key={gwp.offered_variant_type}
                  offeredVariantType={gwp.offered_variant_type}
                />
              ))}
            </GWPSection>
          )}

          {preselectedProducts?.length > 0 && (
            <StyledCartSection>
              <Typography align="center" paragraph variant="h2">
                {content.preselected.title}
              </Typography>

              <Desc align="center" color="grey" variant="p2">
                <span className="remove-in-percy">{content.preselected.deliveryBy}.</span>
              </Desc>

              {renderProductsWithSubscription(preselectedProducts, cartSectionName.PRESELECT)}
            </StyledCartSection>
          )}

          {showPreviewSection && <PreviewSection />}

          {recommendedProducts?.length > 0 && (
            <StyledCartSection>
              <Typography
                align="center"
                className="hide-in-percy"
                data-testid="typography-cart-main-title"
                paragraph
                variant="h2"
              >
                {content.main.title}
              </Typography>
              <Desc align="center" color="grey" variant="p2">
                {content.main.description}
                <span className="remove-in-percy"> {content.main.deliveryBy}.</span>
              </Desc>

              {hasFeedbackFromCheckoutCategory && <ReviewRefineTag />}

              {renderProductsWithSubscription(recommendedProducts, cartSectionName.RECOMMENDED)}
            </StyledCartSection>
          )}

          {upsaleProducts?.length > 0 && (
            <UpsaleSection products={upsaleProducts}>
              <Typography align="center" paragraph variant="h2">
                {content.upsale.title}
              </Typography>

              <Desc align="center" color="grey" variant="p2">
                {content.upsale.description && `${content.upsale.description} `}

                <span className="remove-in-percy">{content.upsale.deliveryBy}.</span>
              </Desc>

              {renderProductsWithSubscription(upsaleProducts, cartSectionName.UPSALE)}
            </UpsaleSection>
          )}

          {featuredProducts?.length > 0 && (
            <FeaturedSection products={featuredProducts}>
              <Typography align="center" paragraph variant="h2">
                {content.featured.title}
              </Typography>

              <Desc align="center" color="grey" variant="p2">
                {content.featured?.description}
                <span className="remove-in-percy">{content.featured.deliveryBy}.</span>
              </Desc>

              {renderProductsWithSubscription(featuredProducts, cartSectionName.FEATURED)}
            </FeaturedSection>
          )}

          {optionalProducts?.length > 0 && (
            <>
              <OptionalSection products={optionalProducts}>
                <OptionalSection.Header>
                  <Typography align="center" color="grey" paragraph variant="h3">
                    {content.optional.title}
                  </Typography>
                  {content.optional?.description && (
                    <Desc align="center" color="grey" variant="p2">
                      {content.optional.description}
                    </Desc>
                  )}
                </OptionalSection.Header>

                <OptionalSection.Body>
                  {arePreviewProductsInOptional && hasUnansweredQuestions
                    ? renderProductsWithSubscription(
                        optionalProducts.filter(
                          p => !productsWithUnansweredQuestionsList.includes(p.type)
                        ),
                        cartSectionName.OPTIONAL
                      )
                    : renderProductsWithSubscription(optionalProducts, cartSectionName.OPTIONAL)}
                </OptionalSection.Body>
              </OptionalSection>

              <DarkBackground />
            </>
          )}
        </ScrollTracker>
      </div>

      <CheckoutTicketV2
        ctaButtonProps={{
          disabled,
          isLoading: isSaving,
          text: 'checkout',
          onClick: goToNextCheckoutStep,
          variant: 'vert',
        }}
      />
    </BaseScene>
  );
};

Cart.propTypes = {
  cart: PropTypes.shape({
    catalog: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    total_price: PropTypes.number.isRequired,
    total_amount: PropTypes.number.isRequired,
    total_shipping: PropTypes.number.isRequired,
    can_subscribe: PropTypes.bool,
  }),
  checkoutCategory: checkoutCategoryProp.isRequired,
  consultationProfileCountry: consultationCountriesProps,
  content: PropTypes.shape({
    preselected: PropTypes.shape({
      title: PropTypes.string.isRequired,
      deliveryBy: PropTypes.string.isRequired,
    }).isRequired,
    main: PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      deliveryBy: PropTypes.string.isRequired,
    }).isRequired,
    featured: PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
      deliveryBy: PropTypes.string.isRequired,
    }).isRequired,
    upsale: PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
      deliveryBy: PropTypes.string,
    }).isRequired,
    optional: PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
      seeMore: PropTypes.string.isRequired,
      seeLess: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  disabled: PropTypes.bool,
  featuredProducts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  hasAnsweredStylingGelQuestions: PropTypes.bool,
  currency: PropTypes.string.isRequired,
  hasUnansweredQuestions: PropTypes.bool,
  productsWithUnansweredQuestionsList: PropTypes.arrayOf(PropTypes.string),
  arePreviewProductsInOptional: PropTypes.bool,
  isCartSubscribeable: PropTypes.bool,
  isReceivingGift: PropTypes.bool,
  isSaving: PropTypes.bool.isRequired,
  optionalProducts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  preselectedProducts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  recommendedProducts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  showPreviewSection: PropTypes.bool,
  updateStatusIsLoading: PropTypes.bool.isRequired,
  trackMembershipModalViewed: PropTypes.func.isRequired,
  upsaleProducts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  userFirstname: PropTypes.string,
  giftWithPurchaseListInCatalog: PropTypes.arrayOf(
    PropTypes.shape({
      offered_variant_type: PropTypes.string.isRequired,
    })
  ),
  trackCartViewed: PropTypes.func.isRequired,
  hasCompletedAllConsultations: PropTypes.bool,
  hasFeedbackFromCheckoutCategory: PropTypes.bool.isRequired,
};

Cart.defaultProps = {
  cart: {
    total_price: 0,
    total_amount: 0,
    total_shipping: 0,
    can_subscribe: false,
  },
  consultationProfileCountry: null,
  disabled: false,
  isCartSubscribeable: false,
  isReceivingGift: false,
  giftWithPurchaseListInCatalog: null,
  showPreviewSection: false,
  userFirstname: null,
  hasAnsweredStylingGelQuestions: false,
  hasUnansweredQuestions: false,
  productsWithUnansweredQuestionsList: [],
  arePreviewProductsInOptional: false,
  hasCompletedAllConsultations: false,
};

export default connect(
  (state, checkoutCategory) => ({
    cart: checkoutCartSelectors.getCartData(state),
    currency: checkoutCartSelectors.getCurrencyFromCart(state),
    consultationProfileCountry:
      checkoutCartSelectors.getConsultationProfileCountryInCartSurvey(state),
    content: checkoutCartSelectors.getSectionsContent(state, checkoutCategory),
    disabled: checkoutCartSelectors.getIsDisabled(state),
    featuredProducts: checkoutCartSelectors.getFeaturedProducts(state),
    hasAnsweredStylingGelQuestions: userSelectors.getHasAnsweredStylingGelQuestions(state),
    hasUnansweredQuestions: userSelectors.getHasUnansweredQuestions(state),
    arePreviewProductsInOptional: checkoutCartSelectors.arePreviewProductsInOptional(state),
    productsWithUnansweredQuestionsList:
      userSelectors.getProductsWithUnansweredQuestionsList(state),
    isCartSubscribeable: checkoutCartSelectors.getIsSubscribeable(state),
    isReceivingGift: checkoutCartSelectors.getIsReceivingGift(state),
    isSaving: checkoutCartSelectors.getIsSaving(state),
    optionalProducts: checkoutCartSelectors.getOptionalProducts(state),
    preselectedProducts: checkoutCartSelectors.getPreselectedProducts(state),
    recommendedProducts: checkoutCartSelectors.getRecommendedProducts(state),
    giftWithPurchaseListInCatalog: checkoutCartSelectors.getGiftWithPurchaseListInCatalog(state),
    showPreviewSection: checkoutCartSelectors.getHasPreviewProducts(state),
    upsaleProducts: checkoutCartSelectors.getUpsaleProducts(state),
    userFirstname: userSelectors.getUserFirstName(state),
    hasCompletedAllConsultations: userSelectors.getHasCompletedAllConsultations(state),
    updateStatusIsLoading: checkoutCartSelectors.getUpdateStatusIsLoading(state),
    hasFeedbackFromCheckoutCategory: userSelectors.getHasFeedbackFromCheckoutCategory(
      state,
      checkoutCategory
    ),
  }),
  {
    trackCartViewed: trackingActions.trackCartViewed,
    trackMembershipModalViewed: trackingActions.trackMembershipModalViewed,
  }
)(Cart);
